import React, { useState, useMemo, useEffect, ReactNode } from 'react';
import { Button, Empty, Form, Input, Key, Modal, Select, Tree } from '@maxtropy/components';
import { apiV2EnergyAnalysisListByOuPost, V2EnergyAnalysisListPostResponse } from '@maxtropy/device-customer-apis-v2';
import { AnalysisUnit } from '@/pages/MapConfiguration/EnergyOverview/components/BindUnitModal';
import type { DataNode } from 'antd/es/tree';
import styles from '@/pages/MapConfiguration/EnergyOverview/components/BindUnitModal/index.module.scss';
import _ from 'lodash';
import { EnergyUnitTreeItem, getEnergyUnitTree } from '@/api/energyUnitMapping';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Divider, Spin } from 'antd';

interface UnitDataNode extends DataNode {
  parentId: string;
}

function formatToNodeData(data: EnergyUnitTreeItem[]): UnitDataNode[] {
  return data.map(i => {
    return {
      title: i.name,
      key: i.energyUnitId,
      children: i.children && i.children.length > 0 ? formatToNodeData(i.children) : [],
      disabled: i.disabled,
      parentId: i.parentId,
    };
  });
}

const dataList: { key: React.Key; title: string }[] = [];

const generateList = (data: DataNode[]) => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    const { key, title } = node;
    dataList.push({ key, title: title as string });
    if (node.children) {
      generateList(node.children);
    }
  }
};

const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    if (node.children) {
      if (node.children.some(item => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

const getParentIdsByNodeId = (data: DataNode[], nodeId: React.Key, path: React.Key[] = []): React.Key[] => {
  for (const node of data) {
    const id = node.key;
    path.push(node.key);
    if (id === nodeId) return path;
    if (node.children) {
      const findChildren = getParentIdsByNodeId(node.children, nodeId, path);
      if (findChildren?.length) return findChildren;
    }
    path.pop();
  }
  return [];
};

const getIds = (data: DataNode, ids: Key[]) => {
  if (!data.disabled) {
    ids.push(data.key as Key);
  }

  if (data.children) {
    for (const child of data.children) {
      getIds(child, ids);
    }
  }
  return ids;
};

export interface unitDataType {
  energyUnitGroupId: number;
  energyUnitList: { energyUnitIds: number; energyUnitName: string }[];
}

interface Props {
  value?: unitDataType;
  onChange: (value: unitDataType) => void;
  unitGroup: AnalysisUnit[];
  onCancel: () => void;
}

export const BindingUnit: React.FC<Props> = ({ value, onChange, unitGroup, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  const [treeNodeData, setTreeNodeData] = useState<UnitDataNode[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>();
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const energyGroupId = Form.useWatch('energyGroupId', form);
  // 已选中的用能单元
  const [unitCheckedKeys, setUnitCheckedKeys] = useState<Key[]>(
    value ? value.energyUnitList.map(item => item.energyUnitIds) : []
  );

  const unitGroupOption = useMemo(() => {
    if (unitGroup?.length) {
      return unitGroup.map(i => ({
        label: i.name,
        value: i.id,
      }));
    }
  }, [unitGroup]);

  const treeData = useMemo(() => {
    const loop = (data: UnitDataNode[]): DataNode[] =>
      data.map(item => {
        const strTitle = item.title as string;
        const index = strTitle.indexOf(searchValue);
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + searchValue.length);
        let title: ReactNode =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className={styles.siteTreeSearchValue}>{searchValue}</span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );

        if (item.children) {
          return {
            title,
            key: item.key,
            children: loop(item.children as UnitDataNode[]),
            disabled: item.disabled,
            selectable: true,
          };
        }
        return {
          title,
          key: item.key,
          disabled: item.disabled,
          selectable: true,
        };
      });

    return loop(treeNodeData);
  }, [searchValue, treeNodeData]);

  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const newExpandedKeys = dataList
      .map(item => {
        if (item.title.indexOf(value) > -1) {
          return getParentKey(item.key, treeNodeData);
        }
        return null;
      })
      .filter((item, i, self) => item && self.indexOf(item) === i);
    const allParentKeys = (newExpandedKeys as React.Key[]).map(key => {
      return getParentIdsByNodeId(treeNodeData, key);
    });
    setExpandedKeys(_.uniq(allParentKeys.flat()));
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  const onCheck = (keys: any) => {
    // 单选模式：只保留最后一个选中的项
    const checkedKeys = keys.checked || keys;
    if (Array.isArray(checkedKeys) && checkedKeys.length > 0) {
      setUnitCheckedKeys([checkedKeys[checkedKeys.length - 1]]);
    } else {
      setUnitCheckedKeys(checkedKeys);
    }
  };

  useEffect(() => {
    if (energyGroupId) {
      setLoading(true);
      getEnergyUnitTree(energyGroupId)
        .then(res => {
          setTreeNodeData(formatToNodeData(res.list ?? []));
          generateList(formatToNodeData(res.list ?? []));
          setExpandedKeys(dataList.map(i => i.key));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [energyGroupId]);

  useEffect(() => {
    if (value) {
      form.setFieldValue('energyGroupId', value.energyUnitGroupId);
      setUnitCheckedKeys(value.energyUnitList.map(item => item.energyUnitIds));
    }
  }, [value]);

  const onBind = () => {
    const energyGroupId = form.getFieldValue('energyGroupId');
    onChange?.({
      energyUnitGroupId: energyGroupId,
      energyUnitList: unitCheckedKeys.map(item => {
        const found = dataList.find(i => String(i.key) === String(item));
        return {
          energyUnitIds: Number(item),
          energyUnitName: found ? found.title : '',
        };
      }),
    });
    onCancel();
  };

  return (
    <Modal
      title="绑定用能单元"
      size="small"
      open={true}
      onCancel={() => {
        form.setFieldValue('energyGroupId', undefined);
        onCancel();
      }}
      onOk={onBind}
    >
      <Form form={form}>
        <Form.Item label="用能分析组" name="energyGroupId">
          <Select placeholder="请选择" options={unitGroupOption} />
        </Form.Item>
        <div className={styles.content}>
          <Form.Item
            extra={
              treeData.length > 0 && (
                <p>
                  {/* @ts-ignore */}
                  <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />
                  &nbsp;灰色选择框表示无该用能单元权限
                </p>
              )
            }
          >
            <div>
              <Input.Search placeholder="请输入关键字" onChange={onInputChange} />
              <Divider style={{ borderColor: 'rgba(255,255,255,0.15)' }} />

              <Spin spinning={loading}>
                {treeData.length > 0 ? (
                  <div className={styles.treeBackground} style={{ overflow: 'auto', maxHeight: 367, maxWidth: 433 }}>
                    <Tree
                      multiple={false}
                      checkable
                      checkStrictly
                      // showLine
                      checkedKeys={unitCheckedKeys}
                      onExpand={onExpand}
                      expandedKeys={expandedKeys}
                      autoExpandParent={autoExpandParent}
                      treeData={treeData}
                      onCheck={onCheck}
                    />
                  </div>
                ) : (
                  <Empty style={{ maxWidth: 433 }} />
                )}
              </Spin>
            </div>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
